import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Plus,
  Loader2,
  AlertCircle,
  FileText,
  SquarePen,
  Trash,
  Layers,
  File,
  ListChecks,
  Check,
  LayoutGrid,
  LayoutList,
  Search,
  Sparkles,
  Table2,
  FileInput,
  type LucideIcon,
} from "lucide-react"
import { useResponsesStore } from "@/stores/responses-store"
import { useDocumentsStore } from "@/stores/documents-store"
import { useExtractionStore } from "@/stores/extraction-store"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { useOutlineTemplatesStore } from "@/stores/outline-templates-store"
import {
  ScheduleSourcePicker,
  type ScheduleSourceKind,
  EMPTY_SCHEDULE_SOURCE_STATE,
  type ScheduleSourceState,
} from "@/components/schedule/ScheduleSourcePicker"
import { stripExtension } from "@/lib/workbook"
import { createWordForm, toastApiError, type BidArtifact, type OutlineTemplateSection } from "@/lib/api"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import { getInitials, getAvatarColor } from "@/lib/avatar"
import { getUserDisplayName } from "@/lib/user-display"
import { useAuthStore } from "@/stores/auth-store"
import { useBidReadOnly } from "@/hooks/useViewOnly"
import { ViewOnlyNotice } from "@/components/ViewOnlyNotice"

// ===================
// Status Configuration
// ===================

const statusConfig: Record<BidArtifact['status'], { label: string; variant: 'secondary' | 'warning' | 'default' | 'success' }> = {
  draft: { label: "Draft", variant: "secondary" },
  in_progress: { label: "In progress", variant: "warning" },
  pending_review: { label: "In review", variant: "warning" },
  changes_requested: { label: "Changes requested", variant: "warning" },
  finalized: { label: "Finalised", variant: "success" },
}

// ===================
// Helper Functions
// ===================

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return "—"
  }
}

/**
 * An `excel_schedule` artifact stores its sheet list under `outlineJson.sheets`
 * (the ingest worker writes it there so the dashboard's outline SQL keeps
 * working) — a different shape from the `sections` every other type carries.
 */
function getSheetCount(artifact: BidArtifact): number {
  const outline = artifact.outlineJson as unknown as { sheets?: unknown[] } | null
  return Array.isArray(outline?.sheets) ? outline.sheets.length : 0
}

function getProgressSummary(artifact: BidArtifact): string {
  if (artifact.artifactType === 'word_form') return "Buyer form"
  if (artifact.artifactType === 'excel_schedule') {
    const sheets = getSheetCount(artifact)
    if (sheets === 0) return "Preparing workbook"
    return `${sheets} sheet${sheets !== 1 ? "s" : ""}`
  }
  if (artifact.artifactType === 'questionnaire') {
    const total = artifact.outlineJson?.sections?.length ?? 0
    return `${total} question${total !== 1 ? "s" : ""}`
  }
  if (artifact.scopeKind === 'artifact') return "Single document"
  const total = artifact.outlineJson?.sections?.length ?? 0
  return `${total} section${total !== 1 ? "s" : ""}`
}

/**
 * An outline with nothing in it is the first thing a new user lands on, and
 * "No sections defined" is a dead end. Return the call to action for that case
 * so the card's metadata line can open the artifact instead of just stating the
 * problem; null means there is real progress to summarise.
 *
 * Excel schedules are excluded — an empty sheet list means the workbook is still
 * being ingested, which the user cannot act on.
 */
function getEmptyOutlineCta(artifact: BidArtifact): string | null {
  if (artifact.artifactType === 'excel_schedule' || artifact.artifactType === 'word_form') return null
  const isEmpty = (artifact.outlineJson?.sections?.length ?? 0) === 0
  // Mirrors getProgressSummary's precedence: type first, then scope.
  if (artifact.artifactType === 'questionnaire') return isEmpty ? "Add questions" : null
  if (artifact.scopeKind === 'artifact') return null
  return isEmpty ? "Add sections" : null
}

function getSectionCount(artifact: BidArtifact): string {
  if (artifact.artifactType === 'word_form') return "—"
  if (artifact.artifactType === 'excel_schedule') {
    const sheets = getSheetCount(artifact)
    return sheets === 0 ? "—" : sheets.toString()
  }
  if (artifact.artifactType === 'questionnaire') {
    return (artifact.outlineJson?.sections?.length ?? 0).toString()
  }
  if (artifact.scopeKind === 'artifact') return "—"
  return (artifact.outlineJson?.sections?.length ?? 0).toString()
}

function getTypeLabel(artifact: BidArtifact): string {
  if (artifact.artifactType === 'excel_schedule') return "Excel schedule"
  if (artifact.artifactType === 'word_form') return "Buyer form"
  if (artifact.artifactType === 'questionnaire') return "Questionnaire"
  return artifact.scopeKind === 'artifact' ? "Single document" : "Multi-section"
}

function getArtifactUrl(artifact: BidArtifact, bidId: string): string {
  if (artifact.artifactType === 'word_form') {
    return `/app/bids/${bidId}/responses/${artifact.id}/form`
  }
  if (artifact.artifactType === 'excel_schedule') {
    return `/app/bids/${bidId}/responses/${artifact.id}/schedule`
  }
  if (artifact.artifactType === 'questionnaire') {
    return `/app/bids/${bidId}/responses/${artifact.id}/questionnaire`
  }
  // Both single-doc and multi-section go to /responses/:artifactId
  // ArtifactDetailPage handles rendering the right overview page
  return `/app/bids/${bidId}/responses/${artifact.id}`
}

// ===================
// New Artifact Dialog
// ===================

type DocumentType = 'multi-section' | 'single' | 'questionnaire' | 'excel-schedule' | 'word-form'

interface DocumentTypeOption {
  type: DocumentType
  icon: LucideIcon
  label: string
  description: string
  /** Optional maturity pill shown beside the label (e.g. "Beta"). */
  badge?: string
}

const DOCUMENT_TYPES: DocumentTypeOption[] = [
  {
    type: 'multi-section',
    icon: Layers,
    label: 'Multi-section',
    description: 'Outline with sections drafted separately',
  },
  {
    type: 'single',
    icon: File,
    label: 'Single document',
    description: 'One continuous document, no outline',
  },
  {
    type: 'questionnaire',
    icon: ListChecks,
    label: 'Questionnaire',
    description: 'Scored Q&A with word limits',
  },
  /**
   * Always offered. It used to appear only once the bid already held a workbook,
   * which made a Beta feature undiscoverable and hid it during the documents
   * fetch. `ScheduleSourcePicker` turns the empty case into an upload rather than
   * a dead end.
   */
  {
    type: 'excel-schedule',
    icon: Table2,
    label: 'Excel schedule',
    description: 'Fill a tender workbook cell by cell',
    badge: 'Beta',
  },
  {
    type: 'word-form',
    icon: FileInput,
    label: 'Word form',
    description: "Fill a buyer's Word form in place",
    badge: 'Beta',
  },
]

/** Document types made from a bid file the user picks or uploads. */
function sourceKindOf(type: DocumentType): ScheduleSourceKind | null {
  if (type === 'excel-schedule') return 'workbook'
  if (type === 'word-form') return 'word'
  return null
}

export interface NewArtifactDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (
    name: string,
    description: string,
    documentType: DocumentType,
    templateSections?: OutlineTemplateSection[],
    sourceDocumentId?: string
  ) => Promise<void>
  isLoading: boolean
  bidId: string
}

export function NewArtifactDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
  bidId,
}: NewArtifactDialogProps) {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [documentType, setDocumentType] = useState<DocumentType>("multi-section")
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("")
  const [sourceState, setSourceState] = useState<ScheduleSourceState>(
    EMPTY_SCHEDULE_SOURCE_STATE
  )
  const submittingRef = useRef(false)

  const { templates, isLoading: isLoadingTemplates, fetchTemplates } = useOutlineTemplatesStore()
  const deleteDocument = useDocumentsStore((s) => s.deleteDocument)

  // Fetch templates when dialog opens with multi-section selected
  useEffect(() => {
    if (open && documentType === 'multi-section') {
      void fetchTemplates()
    }
  }, [open, documentType, fetchTemplates])

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? null
  const sourceKind = sourceKindOf(documentType)
  const needsSource = sourceKind !== null

  const resetForm = () => {
    setName("")
    setDescription("")
    setDocumentType("multi-section")
    setSelectedTemplateId("")
    setSourceState(EMPTY_SCHEDULE_SOURCE_STATE)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    submittingRef.current = true
    try {
      await onSubmit(
        name,
        description,
        documentType,
        selectedTemplate?.sections,
        needsSource ? sourceState.documentId : undefined
      )
      resetForm()
    } finally {
      submittingRef.current = false
    }
  }

  /**
   * Dismissing without creating discards the workbook this dialog uploaded.
   *
   * Reset before deleting: a retained `documentId` would point at a row that no
   * longer exists, and the next Create would 404 on the source lookup.
   *
   * A successful create is NOT a dismissal — `handleCreate` closes the dialog
   * from the parent while `onSubmit` is still awaited here, which arrives as
   * `onOpenChange(false)`. Without the guard that would delete the very workbook
   * the schedule was just created from.
   */
  const handleDismiss = () => {
    if (submittingRef.current) return

    const orphanId = sourceState.uploadedDocId
    resetForm()
    onOpenChange(false)
    if (orphanId) {
      void deleteDocument(orphanId).catch(() => {})
    }
  }

  /** Naming the response after the workbook is right ~always — offer it. */
  const handleSourceChange = (next: ScheduleSourceState) => {
    setSourceState(next)
    if (next.filename && !name.trim()) setName(stripExtension(next.filename))
  }

  const handleDocumentTypeChange = (type: DocumentType) => {
    // A workbook can't seed a Word form (or the reverse): drop the pick, and
    // the file this dialog uploaded for it.
    if (sourceKindOf(type) !== sourceKind) {
      const orphanId = sourceState.uploadedDocId
      setSourceState(EMPTY_SCHEDULE_SOURCE_STATE)
      if (orphanId) void deleteDocument(orphanId).catch(() => {})
    }
    setDocumentType(type)
    if (type !== 'multi-section') {
      setSelectedTemplateId("")
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleDismiss() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Response</DialogTitle>
          <DialogDescription>
            Create a new response document for this bid.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="space-y-4 py-4">
            {/* Document type selection */}
            <div className="space-y-2">
              <Label>Document type</Label>
              <div className="grid grid-cols-2 gap-2">
                {DOCUMENT_TYPES.map(({ type, icon: Icon, label, description, badge }) => {
                  const isSelected = documentType === type
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleDocumentTypeChange(type)}
                      aria-pressed={isSelected}
                      className={cn(
                        "relative flex flex-col items-center gap-1.5 rounded-lg border p-3 text-sm transition-colors",
                        isSelected
                          ? "border-[hsl(var(--brand))] bg-card ring-[3px] ring-[hsl(var(--brand)/0.12)]"
                          : "border-border hover:border-[hsl(var(--brand)/0.4)] hover:bg-muted/30"
                      )}
                    >
                      {isSelected && (
                        <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]">
                          <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
                        </span>
                      )}
                      <Icon
                        className={cn(
                          "h-5 w-5 transition-colors",
                          isSelected ? "text-[hsl(var(--brand))]" : "text-muted-foreground"
                        )}
                      />
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-xs">
                          {label}
                        </span>
                        {badge && (
                          <Badge
                            variant="warning"
                            dot={false}
                            className="text-[9px] px-1.5 py-0 h-4 uppercase tracking-wide"
                          >
                            {badge}
                          </Badge>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground text-center leading-tight">
                        {description}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Template selection — only for multi-section */}
            {documentType === 'multi-section' && (
              <div className="space-y-2">
                <Label>Template (optional)</Label>
                <Select
                  value={selectedTemplateId}
                  onValueChange={(val) => setSelectedTemplateId(val === 'none' ? '' : val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      isLoadingTemplates ? "Loading templates..." : "Start from scratch"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Start from scratch</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name} ({template.sections.length} sections)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Section preview when a template is selected */}
                {selectedTemplate && (
                  <div className="border rounded-lg p-3 bg-muted/30 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium text-muted-foreground">Sections</p>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                        {selectedTemplate.sections.length} sections
                      </Badge>
                    </div>
                    {selectedTemplate.sections.map((section, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground w-4 text-right">{i + 1}.</span>
                        <span className="truncate">{section.title}</span>
                        {section.targetWordCount && (
                          <span className="text-muted-foreground ml-auto flex-shrink-0">
                            {section.targetWordCount} words
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* File selection or upload — Excel schedules and Word forms */}
            {sourceKind && (
              <ScheduleSourcePicker
                key={sourceKind}
                bidId={bidId}
                kind={sourceKind}
                value={sourceState.documentId}
                onChange={handleSourceChange}
                disabled={isLoading}
              />
            )}

            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="e.g., Technical Proposal, Work Methodology"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                placeholder="Brief description of this document..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleDismiss}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                isLoading ||
                !name.trim() ||
                (needsSource && (!sourceState.documentId || sourceState.isUploading))
              }
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Response
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ===================
// Artifact Card
// ===================

interface ArtifactCardProps {
  artifact: BidArtifact
  href: string
  onClick: () => void
  onRename: (artifact: BidArtifact) => void
  onDelete: (artifact: BidArtifact) => void
  progress?: ReactNode
  /** View-only reader: no rename/delete. */
  readOnly?: boolean
}

function ArtifactCard({ artifact, href, onClick, onRename, onDelete, progress, readOnly = false }: ArtifactCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const status = statusConfig[artifact.status]
  const emptyCta = getEmptyOutlineCta(artifact)

  return (
    <div
      className={cn(
        "border rounded-lg p-3 cursor-pointer transition-colors bg-card",
        isHovered ? "bg-muted/50 border-border" : "border-border"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 bg-muted rounded-lg">
          {artifact.artifactType === 'excel_schedule' ? (
            <Table2 className="h-5 w-5 text-muted-foreground" />
          ) : artifact.artifactType === 'word_form' ? (
            <FileInput className="h-5 w-5 text-muted-foreground" />
          ) : artifact.artifactType === 'questionnaire' ? (
            <ListChecks className="h-5 w-5 text-muted-foreground" />
          ) : (
            <FileText className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        {!readOnly && (
        <div className={cn("flex items-center gap-1", !isHovered && "opacity-0")}>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation()
              onRename(artifact)
            }}
          >
            <SquarePen className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            onClick={(e) => {
              e.stopPropagation()
              onDelete(artifact)
            }}
          >
            <Trash className="h-4 w-4" />
          </Button>
        </div>
        )}
      </div>

      <h3 className="font-medium text-sm mb-1">
        <Link
          to={href}
          className="block w-full truncate text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={(e) => e.stopPropagation()}
        >
          {artifact.name}
        </Link>
      </h3>

      <div className="flex items-center gap-2 mb-2">
        <Badge variant={status.variant} className="text-xs whitespace-nowrap">
          {status.label}
        </Badge>
      </div>

      <div className="text-meta">
        {emptyCta ? (
          <button
            type="button"
            className="text-[hsl(var(--link))] hover:underline"
            onClick={(e) => {
              e.stopPropagation()
              onClick()
            }}
          >
            {emptyCta}
          </button>
        ) : !progress ? (
          <span>{getProgressSummary(artifact)}</span>
        ) : null}
        {(emptyCta || !progress) && <span className="mx-1">·</span>}
        <span>Updated {formatDate(artifact.updatedAt)}</span>
      </div>
      {progress && <div className="mt-2">{progress}</div>}
    </div>
  )
}

// ===================
// Main Component
// ===================

interface ResponsesSectionProps {
  bidId: string
  renderProgress?: (artifact: BidArtifact) => ReactNode
}

export function ResponsesSection({ bidId, renderProgress }: ResponsesSectionProps) {
  const navigate = useNavigate()
  const currentUserId = useAuthStore((s) => s.user?.id ?? null)
  // A read-only user — an org viewer, or a member who is not on the bid —
  // opens and reads responses; every create/rename/delete is refused them.
  const { isReadOnly } = useBidReadOnly(bidId)

  // Store state
  const {
    artifacts,
    isLoading,
    error,
    filter,
    setFilter,
    getFilteredArtifacts,
    fetchArtifacts,
    createArtifact,
    updateArtifact,
    deleteArtifact,
    clearError,
  } = useResponsesStore()

  const filteredArtifacts = useMemo(() => getFilteredArtifacts(), [artifacts, filter])

  // ScheduleSourcePicker reads `documents` from the store itself; this fetch is
  // what populates it for the bid.
  const documentsBidId = useDocumentsStore((s) => s.currentBidId)
  const fetchDocuments = useDocumentsStore((s) => s.fetchDocuments)

  useEffect(() => {
    if (bidId && documentsBidId !== bidId) void fetchDocuments(bidId)
  }, [bidId, documentsBidId, fetchDocuments])

  // Mirrors the generator's own precondition (document-assembly.ts): it builds from
  // the submission checklist, falls back to evaluation criteria, and produces nothing
  // when both are empty. Gate on the fact, not on the bid's scope tier — a
  // mis-classified bid that DOES carry a checklist keeps its propose action.
  const extractionCategories = useExtractionStore((s) => s.categories)
  const canPropose =
    extractionCategories.submission_checklist.items.length > 0 ||
    extractionCategories.evaluation_criteria.items.length > 0

  // View mode state (persisted)
  const [viewMode, setViewMode] = useState<'cards' | 'list'>(() => {
    const saved = localStorage.getItem('bidcontext:responses:viewMode')
    return saved === 'cards' ? 'cards' : 'list'
  })

  const handleViewModeChange = (mode: 'cards' | 'list') => {
    setViewMode(mode)
    localStorage.setItem('bidcontext:responses:viewMode', mode)
  }

  // Dialog state
  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<BidArtifact | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [renameTarget, setRenameTarget] = useState<BidArtifact | null>(null)
  const [renameName, setRenameName] = useState("")
  const [isRenaming, setIsRenaming] = useState(false)

  // Fetch data on mount
  useEffect(() => {
    if (bidId) {
      void fetchArtifacts(bidId)
    }
  }, [bidId, fetchArtifacts])

  // AI document proposal (Layer 1): the proposal page resumes an existing
  // unaccepted proposal or starts a new one (?start=1) — never supersedes
  // a pending review by navigating here.
  const handleProposeDocuments = () => {
    if (!bidId) return
    void navigate(`/app/bids/${bidId}/responses/proposal?start=1`)
  }

  // Handlers
  const handleCreate = async (
    name: string,
    description: string,
    documentType: DocumentType,
    templateSections?: OutlineTemplateSection[],
    sourceDocumentId?: string
  ) => {
    if (!bidId) return
    if (documentType === 'word-form') {
      if (!sourceDocumentId) return
      try {
        setIsCreating(true)
        // One form per Word file: an existing one is opened, not duplicated.
        const { artifact, existing } = await createWordForm(bidId, { sourceDocumentId, name })
        if (existing) toast.info("This file already has a form, so we've opened it.")
        setNewDialogOpen(false)
        void navigate(getArtifactUrl(artifact, bidId))
      } catch (error) {
        toastApiError(error, "Couldn't set up this form")
      } finally {
        setIsCreating(false)
      }
      return
    }
    try {
      setIsCreating(true)
      const isSchedule = documentType === 'excel-schedule'
      const scopeKind =
        documentType === 'single' || isSchedule ? 'artifact' as const : 'unit' as const
      const artifactType = isSchedule
        ? 'excel_schedule' as const
        : documentType === 'questionnaire'
          ? 'questionnaire' as const
          : 'narrative_document' as const
      const artifact = await createArtifact(bidId, {
        name,
        description: description || null,
        scopeKind,
        artifactType,
        ...(isSchedule && sourceDocumentId ? { sourceDocumentId } : {}),
      })

      // If a template was selected, save the outline immediately
      if (templateSections && templateSections.length > 0) {
        const { updateArtifactOutline } = await import('@/lib/api')
        const sections = templateSections.map((s, i) => {
          const section: { sectionKey: string; title: string; description?: string; targetWordCount?: number; status: 'not_started' } = {
            sectionKey: `sec-${Date.now()}-${i}`,
            title: s.title,
            status: 'not_started',
          }
          if (s.description) section.description = s.description
          if (s.targetWordCount) section.targetWordCount = s.targetWordCount
          return section
        })
        await updateArtifactOutline(artifact.id, { sections })
      }

      setNewDialogOpen(false)
      void navigate(getArtifactUrl(artifact, bidId))
    } catch {
      // Error is handled by store
    } finally {
      setIsCreating(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      setIsDeleting(true)
      await deleteArtifact(deleteTarget.id)
      setDeleteTarget(null)
    } catch {
      // Error is handled by store
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRename = async () => {
    if (!renameTarget || !renameName.trim()) return
    try {
      setIsRenaming(true)
      await updateArtifact(renameTarget.id, { name: renameName.trim() })
      setRenameTarget(null)
      setRenameName("")
    } catch {
      // Error is handled by store
    } finally {
      setIsRenaming(false)
    }
  }

  const openRenameDialog = (artifact: BidArtifact) => {
    setRenameTarget(artifact)
    setRenameName(artifact.name)
  }

  // Loading state
  if (isLoading && artifacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" onClick={clearError}>
          Try Again
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {artifacts.length === 0 ? (
        // Empty state — AI proposal path primary, manual path untouched beside it
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileText className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="text-base font-medium mb-1">No responses yet</h3>
          {isReadOnly ? (
            <ViewOnlyNotice className="justify-center" />
          ) : (
          <>
          <p className="text-sm text-muted-foreground mb-4 max-w-sm">
            {canPropose
              ? 'Propose the response documents this tender requires from your submission checklist — or start from a blank document.'
              : 'Start from a blank document, or pick one of your outline templates.'}
          </p>
          <div className="flex items-center gap-2">
            {canPropose && (
              <Button onClick={handleProposeDocuments}>
                <GradientSparkleIcon className="h-4 w-4 mr-2" />
                Propose response documents
              </Button>
            )}
            <Button
              variant={canPropose ? "ghost" : "default"}
              onClick={() => setNewDialogOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              New response
            </Button>
          </div>
          <p className="mt-3 text-meta">
            {canPropose
              ? 'Documents from the checklist · outlines come next, inside each document'
              : 'Templates are available inside New response'}
          </p>
          </>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
            <div className="relative w-full sm:w-auto sm:max-w-sm sm:flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search responses..."
                value={filter.search ?? ''}
                onChange={(e) => setFilter(e.target.value ? { search: e.target.value } : {})}
                className="pl-9 h-8"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center border rounded-lg overflow-hidden">
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("h-8 w-8 rounded-none", viewMode === 'list' && "bg-accent")}
                  onClick={() => handleViewModeChange('list')}
                  title="List view"
                >
                  <LayoutList className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn("h-8 w-8 rounded-none", viewMode === 'cards' && "bg-accent")}
                  onClick={() => handleViewModeChange('cards')}
                  title="Card view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
              </div>
              {canPropose && !isReadOnly && (
                <Button
                  variant="ai-outline"
                  size="sm"
                  onClick={handleProposeDocuments}
                  title="Propose additional documents from unlinked checklist items"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Propose documents
                </Button>
              )}
              {!isReadOnly && (
                <Button size="sm" onClick={() => setNewDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New
                </Button>
              )}
            </div>
          </div>

          {filteredArtifacts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-muted-foreground">No matching responses</p>
            </div>
          ) : viewMode === 'list' ? (
            <div className="border rounded-lg overflow-x-auto bg-card shadow-sm">
              <table className="w-full min-w-[48rem]">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3">Name</th>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 w-[130px]">Type</th>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 w-[110px]">Status</th>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 w-[50px]">Owner</th>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 w-[160px]">{renderProgress ? "Progress" : "Sections"}</th>
                    <th className="text-left text-sm font-medium text-muted-foreground px-4 py-3 w-[140px]">Last Updated</th>
                    <th className="w-[80px] py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredArtifacts.map((artifact) => {
                    const status = statusConfig[artifact.status]
                    return (
                      <tr
                        key={artifact.id}
                        className="group cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => void navigate(getArtifactUrl(artifact, bidId))}
                      >
                        <td className="px-4 py-2.5 text-sm max-w-0">
                          <Link
                            to={getArtifactUrl(artifact, bidId)}
                            className="block w-full truncate text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {artifact.name}
                          </Link>
                        </td>
                        <td className="px-4 py-2.5 text-meta">{getTypeLabel(artifact)}</td>
                        <td className="px-4 py-2.5">
                          <Badge variant={status.variant} className="text-xs whitespace-nowrap">{status.label}</Badge>
                        </td>
                        <td className="px-4 py-2.5">
                          {artifact.ownerId ? (
                            (() => {
                              const color = getAvatarColor(artifact.ownerId)
                              const ownerLabel = getUserDisplayName(
                                { userId: artifact.ownerId, fullName: artifact.ownerName },
                                currentUserId,
                                { fallback: 'Owner' }
                              )
                              return artifact.ownerAvatarUrl ? (
                                <img
                                  src={artifact.ownerAvatarUrl}
                                  alt={ownerLabel}
                                  className="h-6 w-6 rounded-full object-cover"
                                  title={ownerLabel}
                                />
                              ) : (
                                <div
                                  className={cn("h-6 w-6 rounded-full flex items-center justify-center font-medium text-[10px]", color.bg, color.text)}
                                  title={ownerLabel}
                                >
                                  {getInitials(artifact.ownerName)}
                                </div>
                              )
                            })()
                          ) : (
                            <span className="text-meta">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-meta">{renderProgress ? renderProgress(artifact) : getSectionCount(artifact)}</td>
                        <td className="px-4 py-2.5 text-meta">{formatDate(artifact.updatedAt)}</td>
                        <td className="px-4 py-2.5">
                          {!isReadOnly && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={(e) => {
                                e.stopPropagation()
                                openRenameDialog(artifact)
                              }}
                            >
                              <SquarePen className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteTarget(artifact)
                              }}
                            >
                              <Trash className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filteredArtifacts.map((artifact) => (
                <ArtifactCard
                  key={artifact.id}
                  artifact={artifact}
                  href={getArtifactUrl(artifact, bidId)}
                  onClick={() => void navigate(getArtifactUrl(artifact, bidId))}
                  onRename={openRenameDialog}
                  onDelete={setDeleteTarget}
                  progress={renderProgress?.(artifact)}
                  readOnly={isReadOnly}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* New Artifact Dialog */}
      <NewArtifactDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        onSubmit={handleCreate}
        isLoading={isCreating}
        bidId={bidId}
      />

      {/* Rename Dialog */}
      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => !open && setRenameTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Response</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="rename">Name</Label>
            <Input
              id="rename"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button onClick={() => void handleRename()} disabled={isRenaming || !renameName.trim()}>
              {isRenaming && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Response?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.name}" and all its
              sections. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
